import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Option } from 'slim-select/dist/data.d';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { RootState } from '../../../../core/state/state';
import Button from '../../../../components/form/button/Button';
import { Color } from '../../../../core/enums/Color';
import Modal from '../../../components/modal/Modal';
import Select from '../../../../components/form/select/Select';
import Radio from '../../../../components/form/radio/Radio';
import Input from '../../../../components/form/input/Input';
import { Transport } from '../../../../core/state/zones/types';
import { manageDeliveryZone, getDeliveryZones } from '../../../../core/state/zones/asyncActions';
import { spawnGlobalNotification } from '../../../../core/state/global-notifications/asyncActions';
import { GlobalNotificationTypes } from '../../../../core/enums/GlobalNotificationTypes';
import Badge from '../../../components/badge/Badge';
import deliveryZoneColorMap from '../../../../core/constants/deliveryZoneColorMap';
import { Size } from '../../../../core/enums/Size';
import { NotificationPosition } from '../../../../core/state/global-notifications/types';
import { ZoneMode } from '../../../../core/services/ZonesService';
import CancelAllBookingsModal from '../cancel-all-bookings/CancelAllBookingsModal';
import { getProjectSettings } from '../../../../core/state/project/asyncActions';
import { isReadAllRole } from '../../../../core/state/user/selectors';
import ProjectSettingsService from '../../../../core/services/ProjectSettingsService';
import { ProjectBarrierDevice, ProjectDeliveryZoneSettings } from '../../../../core/state/project/types';
import { BarrierDeviceMode } from '../../../../core/state/barriers/types';

import './ManageDeliveryZoneModal.scss';

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  order: number;
  editMode?: boolean;
  deliveryZoneId?: number;
}

type FormData = {
  zone: string;
  address: string;
  transport: string;
  status: string;
  barrierSystem: string;
  barrierDeviceId: string;
};

const ManageDeliveryZoneModal: React.FC<Props> = ({ isOpen, closeModal, order, editMode, deliveryZoneId }) => {
  const { t } = useTranslation();
  const { meansOfTransport } = useSelector((state: RootState) => state.zones);
  const zone = useSelector((state: RootState) => state.zones.deliveryZones?.find((item) => item.id === deliveryZoneId) ?? null);
  const dispatch = useDispatch();
  const { projectId } = useParams<{ projectId: string }>();
  const barrierSystemStatus = useSelector((state: RootState) => state.projects.projectSettings?.isInBarrierSystem);
  const [isCancelModalOpen, setIsCancelModalOpen] = React.useState<boolean>(false);
  const [deliveryZoneSettings, setDeliveryZoneSettings] = React.useState<ProjectDeliveryZoneSettings>();
  const isUserReader = useSelector(isReadAllRole);

  React.useEffect(() => {
    if (!editMode) {
      dispatch(getProjectSettings(Number(projectId)));
    }
  }, [dispatch, projectId, editMode]);

  React.useEffect(() => {
    async function fetchDeliveryZones() {
      try {
        const response = await ProjectSettingsService.getDeliveryZoneSettings(Number(projectId));
        setDeliveryZoneSettings(response.data.content);
      } catch (e) {
        // Handle error
      }
    }
    fetchDeliveryZones();
  }, [projectId]);

  const meansOfTransportToOption = (item: Transport): Option => ({
    value: item.id.toString(),
    text: item.name
  });

  const transportOptions = useMemo(() => meansOfTransport?.map(meansOfTransportToOption), [meansOfTransport]);

  const innerHtmlInventoryNumber = (projectBarrierDevice: ProjectBarrierDevice) => {
    let className = '';
    switch (projectBarrierDevice.mode) {
      case BarrierDeviceMode.Online:
        className = 'badge--green';
        break;
      case BarrierDeviceMode.Offline:
        className = 'badge--red';
        break;
      default:
        className = '';
    }
    return `
    <div>
     <span class="pr-3">${projectBarrierDevice.serialNumber} (${projectBarrierDevice.inventoryNumber})</span>
     <div class="badge badge--medium py-1 ${className}">
        <div class="badge__label">(${projectBarrierDevice.mode})</div>
     </div>
    </div>
     `;
  };

  const inventoryNumberOptions = useMemo(() => {
    return deliveryZoneSettings?.projectBarrierDevices.map(
      (projectBarrierDevice: ProjectBarrierDevice): Option => ({
        text: innerHtmlInventoryNumber(projectBarrierDevice),
        value: `${projectBarrierDevice.id}`,
        innerHTML: innerHtmlInventoryNumber(projectBarrierDevice)
      })
    );
  }, [deliveryZoneSettings]);

  const getBerrierSystemStatus = () => {
    if (!editMode) {
      return barrierSystemStatus ? t('common.on') : t('common.off');
    }
    return zone && zone.barrierSystemStatus ? t('common.on') : t('common.off');
  };

  const validationSchema = yup
    .object()
    .shape({
      zone: yup.string().required(),
      address: yup.string().nullable(),
      transport: yup.string().required(),
      status: yup.string().required(),
      barrierSystem: yup.string().required(),
      barrierDeviceId: yup.string().when(['barrierSystem', 'status'], {
        is: (barrSystem: string, status: string) =>
          barrSystem === t('common.on') &&
          status === t('common.active') &&
          deliveryZoneSettings?.projectBarrierDevices &&
          deliveryZoneSettings?.projectBarrierDevices?.length > 0,
        then: yup.string().required()
      })
    })
    .required();

  const {
    handleSubmit,
    control,
    watch,
    formState: { isValid }
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      zone: zone?.name ?? '',
      address: zone?.address ?? '',
      transport: zone && zone.equipment ? String(zone.equipment[0].id) : '',
      status: zone && !zone.active ? t('common.inactive') : t('common.active'),
      barrierSystem: getBerrierSystemStatus(),
      barrierDeviceId: zone?.barrierDevice?.id ? String(zone?.barrierDevice?.id) : undefined
    },
    resolver: yupResolver(validationSchema)
  });

  const barrierSystemOptions = useMemo(
    () => [
      {
        label: t('common.off'),
        value: t('common.off')
      },
      {
        label: t('common.on'),
        value: t('common.on')
      }
    ],
    [t]
  );

  const zoneStatusOptions = useMemo(
    () => [
      {
        label: t('common.active'),
        value: t('common.active')
      },
      {
        label: t('common.inactive'),
        value: t('common.inactive')
      }
    ],
    [t]
  );

  const manageDeliveryZoneSuccess = () => {
    dispatch(
      spawnGlobalNotification({
        type: GlobalNotificationTypes.Success,
        message: editMode ? t('modal.manageDeliveryZone.form.edit.message.success') : t('modal.manageDeliveryZone.form.message.success'),
        title: editMode ? t('common.notification.success') : t('modal.manageDeliveryZone.form.message.success.title'),
        postition: NotificationPosition.Top
      })
    );
  };

  const changeBarrierStatusSuccess = () => {
    dispatch(
      spawnGlobalNotification({
        type: GlobalNotificationTypes.Success,
        message: t('modal.manageDeliveryZone.form.barrierChanged.message.success'),
        title: t('common.notification.success'),
        postition: NotificationPosition.Top
      })
    );
  };

  const submitManageDeliveryZone = (formData: FormData) => {
    return dispatch<any>(
      manageDeliveryZone(
        Number(projectId),
        formData.status !== t('common.inactive'),
        formData.zone,
        formData.address,
        formData.barrierSystem !== t('common.off'),
        Number(formData.transport),
        editMode ? ZoneMode.Edit : ZoneMode.Create,
        deliveryZoneId,
        formData.barrierDeviceId ? Number(formData.barrierDeviceId) : undefined
      )
    );
  };

  const closeModalDialog = () => {
    dispatch(
      spawnGlobalNotification({
        type: GlobalNotificationTypes.Info,
        message: editMode ? t('modal.manageDeliveryZone.form.edit.message.cancel.text') : t('modal.manageDeliveryZone.form.message.cancel'),
        title: editMode ? t('common.changesDiscarded') : t('modal.manageDeliveryZone.form.message.cancel.title'),
        postition: NotificationPosition.Top
      })
    );
    closeModal();
  };

  const onSubmit = async (formData: FormData) => {
    if (zone?.barrierDevice && formData.barrierSystem === t('common.off')) {
      dispatch(
        spawnGlobalNotification({
          type: GlobalNotificationTypes.Warning,
          title: t('common.attention'),
          autoHide: false,
          buttons: {
            confirm: {
              text: t('common.proceed'),
              onClick: async () => {
                try {
                  const response = await submitManageDeliveryZone(formData);

                  if (response) {
                    await dispatch(getDeliveryZones(Number(projectId)));
                    changeBarrierStatusSuccess();
                    closeModal();
                  } else {
                    setIsCancelModalOpen(true);
                  }
                } catch (e) {
                  // Error handler
                }
              }
            },
            cancel: {
              text: t('common.cancel'),
              onClick: () => {
                closeModalDialog();
              }
            }
          },
          closeButton: false,
          message: t('modal.manageDeliveryZone.form.barrierChanged.message.warning')
        })
      );
    } else {
      const response = await submitManageDeliveryZone(formData);

      if (response) {
        await dispatch(getDeliveryZones(Number(projectId)));
        manageDeliveryZoneSuccess();
        closeModal();
      } else {
        setIsCancelModalOpen(true);
      }
    }
  };

  const title = <h3 className="modal-title">{editMode ? t('modal.manageDeliveryZone.edit.title') : t('modal.manageDeliveryZone.title')}</h3>;
  const buttons = (
    <>
      {!isUserReader && (
        <div className="modal-buttons">
          <Button color={Color.Blue} buttonTypes={['transparent']} onClick={closeModalDialog}>
            <span>{t('common.cancel')}</span>
          </Button>
          <Button disabled={!isValid} form="manage-delivery-zone" type="submit" buttonTypes={['blue']}>
            {editMode ? t('common.save') : t('common.create')}
          </Button>
        </div>
      )}
    </>
  );

  const barrierSystem = watch('barrierSystem');
  const zoneStatus = watch('status');

  return (
    <Modal isOpen={isOpen} closeModal={closeModal} title={title} toolbar={buttons} size="small" height={526}>
      <form id="manage-delivery-zone" className="manage-delivery-zone-modal" onSubmit={handleSubmit(onSubmit)}>
        <div className="manage-delivery-zone-modal__content">
          <div className="manage-delivery-zone-modal__order">
            <Badge icon={order} color={deliveryZoneColorMap[order]} boldLabel size={Size.Large} />
          </div>
          <Controller
            name="zone"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <Input
                label={t('modal.manageDeliveryZone.form.option.zone')}
                focused
                required
                id="zone"
                name={name}
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
              />
            )}
          />
          <Controller
            name="address"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <Input label={t('common.address')} id="address" name={name} type="text" value={value} onChange={(e) => onChange(e.target.value)} />
            )}
          />
          <Controller
            name="transport"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <Select
                id="transport"
                label={t('modal.manageDeliveryZone.form.option.transport')}
                name={name}
                value={value}
                options={transportOptions}
                onChange={(option: any) => {
                  if (Array.isArray(option)) {
                    [option] = option; // eslint-disable-line no-param-reassign
                  }
                  onChange(option.value !== 'undefined' ? option.value : undefined);
                }}
                addPlaceholderOption
                required
              />
            )}
          />
          <div className="d-flex">
            <Controller
              name="barrierSystem"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <Radio
                  id="barrierSystem"
                  className="manage-delivery-zone-modal__barrier-system"
                  name={name}
                  label={t('modal.manageDeliveryZone.form.option.barrierSystem')}
                  required
                  options={barrierSystemOptions}
                  defaultValue={value}
                  onChange={onChange}
                />
              )}
            />
            <Controller
              name="status"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <Radio
                  id="status"
                  name={name}
                  required
                  label={t('modal.manageDeliveryZone.form.option.status')}
                  options={zoneStatusOptions}
                  defaultValue={value}
                  onChange={onChange}
                />
              )}
            />
          </div>
          <div>
            {barrierSystem === t('common.on') &&
              zoneStatus === t('common.active') &&
              deliveryZoneSettings?.projectBarrierDevices &&
              deliveryZoneSettings?.projectBarrierDevices.length > 0 && (
                <Controller
                  name="barrierDeviceId"
                  control={control}
                  render={({ field: { onChange, value, name } }) => (
                    <Select
                      id="barrierDeviceId"
                      name={name}
                      label={t('modal.manageDeliveryZone.form.option.serialNumber')}
                      showSearch={false}
                      value={String(value)}
                      onChange={(option) => {
                        if (Array.isArray(option)) {
                          [option] = option; // eslint-disable-line no-param-reassign
                        }
                        onChange(option.value !== 'undefined' ? option.value : undefined);
                      }}
                      options={inventoryNumberOptions}
                      addPlaceholderOption
                      showErrorMessage
                      required
                    />
                  )}
                />
              )}
          </div>
        </div>
      </form>
      {isCancelModalOpen && (
        <CancelAllBookingsModal type="DeactivatingZone" isOpen={isCancelModalOpen} closeModal={() => setIsCancelModalOpen(false)} />
      )}
    </Modal>
  );
};

export default ManageDeliveryZoneModal;
